<template>
  <busy-area :busy="loading">
    <accordion>
      <accordion-item
        v-for="(translation, index) in translations"
        :key="translation.language"
        :open="(openLanguage && openLanguage === translation.language) || (!openLanguage && index === 0)"
        :deleteable="!alwaysShowAllLanguages"
        :title="$t(`language.${translation.language}`)"
        @delete="deleteLanguage(translation.language)"
      >
        <slot :translation="translation" />
      </accordion-item>
    </accordion>
    <div v-if="!alwaysShowAllLanguages" class="flex gap-x-5" :class="{ 'mt-6': translations.length > 0 }">
      <CustomButton
        v-for="lang in unusedLanguages"
        :key="lang"
        variant="primary"
        class="py-2 px-5"
        @click="addLanguage(lang)"
      >
        {{ `+ ${toUpperCase(lang)}` }}
      </CustomButton>
    </div>
  </busy-area>
</template>

<script lang="ts" setup>
import {computed, ref, type Ref, watch} from 'vue';
import BusyArea from '@utils/busy/BusyArea.vue';
import Accordion from '@utils/accordion/Accordion.vue';
import AccordionItem from '@utils/accordion/AccordionItem.vue';
import { useVModel } from '@vueuse/core';
import { sortBy } from 'lodash';
import { useI18n } from 'vue-i18n';
import CustomButton from '@utils/CustomButton.vue';
import { toUpperCase } from 'uri-js/dist/esnext/util';
import { useFetchQuery } from '@/hooks/fetch';
import type { TranslationEntity } from '@/entities/Entities';
import type { ValidatedTree } from '@/hooks/form';

interface NewTranslationEntity {
  language: string;
}

interface TranslatedEntity {
  translations: Array<NewTranslationEntity | TranslationEntity>;
}

interface Props {
  modelValue: TranslatedEntity;
  errors?: Ref<ValidatedTree | undefined>;
  alwaysShowAllLanguages?: boolean;
  defaultValuesForNewTranslations?: Record<string, any>;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const { t } = useI18n();

const translations = computed(() =>
  sortBy(value.value.translations, [(trans) => t(`language.${trans.language}`)]));

const getTranslation = (language: string) =>
  (value.value.translations ?? []).find((trans) => trans.language === language);

const { data: languages, loading } = useFetchQuery<string[]>('/enums/Language');

const unusedLanguages = computed(() =>
  sortBy(
    languages.value?.filter((language) => !getTranslation(language)),
    value.value.translations,
    [(lang) => t(`language.${lang}`)],
  ));

const openLanguage = ref<undefined | string>(undefined);

if (props.alwaysShowAllLanguages) {
  watch(languages, (langs) => {
    if (langs?.length) {
      for (const language of langs) {
        const translation = getTranslation(language);
        if (!translation) {
          if (!value.value.translations) {
            value.value.translations = [];
          }
          value.value.translations.push({ language });
        }
      }
    }
  });
}

const addLanguage = (language: string) => {
  if (!value.value.translations) {
    value.value.translations = [];
  }
  const newLang = {
    language,
  };
  if (props.defaultValuesForNewTranslations) {
    Object.assign(newLang, props.defaultValuesForNewTranslations);
  }
  value.value.translations.push(newLang);
  openLanguage.value = language;
};

const deleteLanguage = (language: string) => {
  value.value.translations = value.value.translations.filter((item) => item.language !== language);
};
</script>
